<template>
	<div class="page task-detail-page">
		<v-breadcrumbs
			:items="breadcrumbs"
			class="sgnl-p-0 sgnl-m-0"
		/>
		<ErrorEmptyState
			v-if="loadError"
			title="Could not load Task"
			message="Often this is a temporary network or connectivity issue."
			:retrying="retryingFetch"
			bordered
			@retry="retryFetchTask"
		/>
		<section
			v-else
			class="task-detail-grid"
		>
			<div class="grid-taskCard">
				<TaskCard
					v-if="taskID"
					:taskData="taskData ?? undefined"
					:taskID="taskID"
					:show-delete-task-button="true"
					:show-error-details-tooltip="false"
					@removeTaskSuccess="getBackToTaskList"
				/>
			</div>
			<div class="grid-resultsPanel">
				<FetchErrorBanner
					v-if="outputFetchError"
					:accessState="outputFetchError"
					@retry="retryOutputFetch"
				/>
				<Panel
					:taskID="taskID"
					:buildID="buildID"
					:showCopyButton="true"
					:showClearButton="false"
				/>
			</div>
		</section>
	</div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

// Types
import { isTaskActive, Task } from "@/types/api/tasks";

// Libraries

// Utilities
import { sdk } from "@/js/sdk";
import { SdkError, ERROR_CODES } from "@signaloid/cloud-compute-engine-client";
import { monitoringCaptureError } from "@/plugins/monitoring";

// Components
import Panel from "@/components/Panel.vue";
import TaskCard from "@/components/TaskManager/TaskCard.vue";
import FetchErrorBanner from "@/components/Common/FetchErrorBanner.vue";
import ErrorEmptyState from "@/components/Common/ErrorEmptyState.vue";

// Stores
import { useTasksStore } from "@/stores/tasks";
import { RemoteAccessState, ResultsPanelTab, TaskBusEvent } from "@/types/general";

// Global variables
import { defaultTabs } from "@/assets/defaultTabs";
import { useOutputsStore } from "@/stores/outputs";
import { Build } from "@/types/api/builds";
import { useBuildsStore } from "@/stores/builds";

// Local Types
type ComponentData = {
	taskData: undefined | null | Task;
	buildData: undefined | null | Build;
	taskID: undefined | string;
	buildID: undefined | string;
	results: undefined | ResultsPanelTab[];
	resultsLoading: boolean;
	taskEventBusSub: undefined | Function;
	loadError: boolean;
	retryingFetch: boolean;
};

type Breadcrumb = {
	title: string;
	disabled: boolean;
	exact?: boolean;
	to?: {
		name: string;
	};
};

export default defineComponent({
	name: "TaskDetail",
	components: { Panel, TaskCard, FetchErrorBanner, ErrorEmptyState },
	data: (): ComponentData => ({
		taskID: undefined,
		buildID: undefined,
		taskData: undefined,
		buildData: undefined,
		results: undefined,
		resultsLoading: false,
		taskEventBusSub: undefined,
		loadError: false,
		retryingFetch: false,
	}),
	setup() {
		const tasksStore = useTasksStore();
		const buildsStore = useBuildsStore();
		const taskOutputsStore = useOutputsStore();
		return { tasksStore, taskOutputsStore, buildsStore };
	},
	computed: {
		resultsPanelText(): readonly ResultsPanelTab[] {
			return this.results ?? defaultTabs;
		},
		breadcrumbs(): Breadcrumb[] {
			const detailTitle = this.taskData?.BuildID
				? `Task Detail - Build: ${this.taskData.BuildID}`
				: "Task Detail";
			return [
				{ title: "Task Manager", disabled: false, exact: true, to: { name: "TaskManager" } },
				{ title: detailTitle, disabled: true },
			];
		},
		outputFetchError(): RemoteAccessState | undefined {
			const taskState = this.taskOutputsStore.getTaskOutputAccessState(this.taskID);
			if (taskState?.error) return taskState;
			const buildState = this.taskOutputsStore.getBuildOutputAccessState(this.buildID);
			if (buildState?.error) return buildState;
			return undefined;
		},
	},
	methods: {
		async retryOutputFetch() {
			await this.taskOutputsStore.retryOutputs({
				taskID: this.taskID,
				buildID: this.buildID,
			});
		},
		async loadTask() {
			if (!this.taskID) return;
			this.taskData = this.tasksStore.getTaskByID(this.taskID);

			if (!this.taskData) {
				try {
					const response = await sdk.tasks.getOne(this.taskID);
					this.taskData = response as Task;
					this.loadError = false;
				} catch (error) {
					if (error instanceof SdkError && error.code === ERROR_CODES.API_NOT_FOUND) {
						console.warn(`Task with ID ${this.taskID} not found! Redirecting to task manager`);
						this.$router.push({ name: "TaskManager" });
						return;
					}
					this.loadError = true;
					monitoringCaptureError(error, "Fetch details of a historical task");
					return;
				}
			}

			if (!this.taskData) {
				console.warn(`Task with ID ${this.taskID} not found! Redirecting to task manager`);
				this.$router.push({ name: "TaskManager" });
				return;
			}

			this.buildID = this.taskData.BuildID;

			if (isTaskActive(this.taskData.Status) && this.taskEventBusSub === undefined) {
				this.taskEventBusSub = this.tasksStore.taskEventBus.on(this.taskEventBusCallback);
			}
		},
		async retryFetchTask() {
			this.retryingFetch = true;
			try {
				await this.loadTask();
			} finally {
				this.retryingFetch = false;
			}
		},
		async taskEventBusCallback(event: TaskBusEvent) {
			// console.log("taskEventBusSub event (in task detail): ", event);
			// @ts-ignore FIXME:
			if (this.taskID && event.taskData.TaskID === this.taskID) {
				// console.log("update for this task :>> status = ", event.taskData.Status);
				this.taskData = event.taskData;

				this.buildData = this.buildsStore.getBuildByID(event.taskData.BuildID);
			}
		},
		getBackToTaskList() {
			this.$router.push(`/tasks`);
		},
	},
	async created() {
		this.taskID = Array.isArray(this.$route.params.id) ? this.$route.params.id[0] : this.$route.params.id;
		this.resultsLoading = true;
		await this.loadTask();
	},
	beforeUnmount() {
		// Unsubscribe from the task even bus on dismount
		if (this.taskEventBusSub !== undefined) {
			// console.log("Unsubscribing to task Bus (in task detail) :>> ");
			this.taskEventBusSub();
		}
	},
});
</script>

<style lang="scss" scoped>
.task-detail-page {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-8);
}

.task-detail-grid {
	min-width: 350px;
	display: grid;
	gap: var(--sgnl-space-8);

	grid-template-columns: minmax(300px, 1fr);
	grid-template-rows: auto 1fr;
	grid-template-areas:
		"taskCard"
		"resultsPanel";
}

.grid-taskCard {
	grid-area: taskCard;
}
.grid-resultsPanel {
	grid-area: resultsPanel;
	min-height: 70vh;
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-8);
}
</style>
